/**
 * SalesDashboard - monthly orders written / customer POs received / invoiced. Route: /sales (staff login)
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { BarChart3, ChevronLeft, ChevronRight, Factory, FileCheck2, Home, Hourglass, Loader2, PenLine, Percent, Receipt, RefreshCw } from "lucide-react";
import { MetricCard } from "./MetricCard";
import { TrendChart } from "./TrendChart";
import { OrdersTable } from "./OrdersTable";
import { ZeroValuePanel } from "./ZeroValuePanel";
import { RepTable } from "./RepTable";
import { TopSuppliers } from "./TopSuppliers";
import { InvoicesPanel } from "./InvoicesPanel";
import { DepartmentPanel } from "./DepartmentPanel";
import { ImportInvoicesButton } from "./ImportInvoicesButton";
import { InvoicedOverrideButton } from "./InvoicedOverride";
import { eur, monthLabel, shortDate } from "./format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const thisMonth = () => new Date().toISOString().slice(0, 7);
const monthFromUrl = () => {
  const m = new URLSearchParams(window.location.search).get("month");
  return m && /^\d{4}-(0[1-9]|1[0-2])$/.test(m) && m <= thisMonth() ? m : thisMonth();
};
const stepMonth = (ym, delta) => {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export default function SalesDashboard({ onSessionExpired }) {
  const [month, setMonth] = useState(monthFromUrl());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async (ym) => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/sales/dashboard`, {
        params: { month: ym },
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 401 && onSessionExpired) {
        // Cookie/token expired: drop the stale local session so the router sends us back to /login?next=/sales
        sessionStorage.setItem("authNotice", "Your session has expired — please log in again to see the Sales Dashboard.");
        onSessionExpired();
        return;
      }
      setError(err.response?.data?.detail || "Could not load sales figures.");
    } finally {
      setLoading(false);
    }
  }, [onSessionExpired]);

  useEffect(() => {
    load(month);
    const url = new URL(window.location.href);
    if (month === thisMonth()) url.searchParams.delete("month"); else url.searchParams.set("month", month);
    window.history.replaceState(null, "", url);
  }, [month, load]);

  const current = data?.today?.slice(0, 7) || thisMonth();
  const atCurrent = month >= current;
  const s = data?.summary;
  const p = data?.previous;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="sales-dashboard">
      <div className="pointer-events-none fixed inset-0 opacity-[0.12]" style={{ backgroundImage: "linear-gradient(#334155 1px, transparent 1px), linear-gradient(90deg, #334155 1px, transparent 1px)", backgroundSize: "44px 44px" }} />
      <div className="relative mx-auto max-w-7xl px-5 py-8 sm:py-10">
        <header className="mb-8 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500 to-emerald-600 shadow-lg shadow-cyan-900/40">
              <BarChart3 className="h-7 w-7 text-white" />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-400">Business Print</p>
              <h1 className="font-heading text-3xl font-black tracking-tight sm:text-4xl">Sales Dashboard</h1>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center rounded-xl bg-slate-900/80 ring-1 ring-white/10" data-testid="sales-month-picker">
              <button onClick={() => setMonth((m) => stepMonth(m, -1))} className="p-2.5 text-slate-400 hover:text-white" aria-label="Previous month" data-testid="sales-month-prev"><ChevronLeft className="h-5 w-5" /></button>
              <label className="relative px-2">
                <span className="font-heading text-lg font-bold" data-testid="sales-month-label">{monthLabel(month)}</span>
                <input type="month" value={month} max={current} onChange={(e) => e.target.value && setMonth(e.target.value)} className="absolute inset-0 cursor-pointer opacity-0" aria-label="Pick month" data-testid="sales-month-input" />
              </label>
              <button onClick={() => setMonth((m) => stepMonth(m, 1))} disabled={atCurrent} className="p-2.5 text-slate-400 hover:text-white disabled:opacity-30 disabled:hover:text-slate-400" aria-label="Next month" data-testid="sales-month-next"><ChevronRight className="h-5 w-5" /></button>
            </div>
            <button onClick={() => load(month)} className="flex items-center gap-2 rounded-xl bg-slate-900/80 px-3.5 py-2.5 text-sm font-semibold text-slate-300 ring-1 ring-white/10 hover:text-white" data-testid="sales-refresh-btn">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <ImportInvoicesButton onImported={() => load(month)} />
            <a href="/hub" className="flex items-center gap-2 rounded-xl bg-slate-900/80 px-3.5 py-2.5 text-sm font-semibold text-slate-300 ring-1 ring-white/10 hover:text-white" data-testid="sales-hub-link">
              <Home className="h-4 w-4" /> Hub
            </a>
          </div>
        </header>

        {error && (
          <div className="mb-6 rounded-xl bg-rose-950/40 px-4 py-3 text-sm text-rose-300 ring-1 ring-rose-500/30" data-testid="sales-error">{error}</div>
        )}

        {loading && !data ? (
          <div className="flex items-center justify-center py-32 text-slate-400" data-testid="sales-loading"><Loader2 className="mr-2 h-6 w-6 animate-spin" /> Loading figures…</div>
        ) : s && (
          <div className={`space-y-6 transition-opacity ${loading ? "opacity-60" : "opacity-100"}`}>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              <MetricCard id="orders" label="Orders written" hint="By date received" value={s.orders_value} count={s.orders_count} countNoun="order" previousValue={p?.orders_value} accent="#22d3ee" icon={PenLine} />
              <MetricCard id="cost" label="Supplier PO costs" hint={`Purchase orders on orders written in ${monthLabel(month, "short")}`} value={s.cost_value} count={s.cost_po_count} countNoun="supplier PO" previousValue={p?.cost_value} invertDelta accent="#fb7185" icon={Factory}
                footnote={`Across ${s.cost_job_count} of ${s.orders_count} order${s.orders_count === 1 ? "" : "s"} · stock used at €0 and void POs excluded`} />
              <MetricCard id="margin" label="Gross margin" hint="Orders written − supplier PO costs" value={s.margin_value} previousValue={p?.margin_value} accent="#fcd34d" icon={Percent}
                countLabel={s.margin_pct === null ? "no sales yet" : `${s.margin_pct}% of sales`}
                footnote="Internal labour and materials from stock are not costed — this is margin after bought-in supplier costs only" />
              <MetricCard id="po" label="Customer PO value" hint="Orders where a PO was received this month" value={s.po_value} count={s.po_count} countNoun="PO" previousValue={p?.po_value} accent="#f59e0b" icon={FileCheck2} />
              <MetricCard id="invoiced" label="Invoiced" tag={s.invoiced_basis === "manual" ? "Manual entry" : undefined}
                hint={s.invoiced_basis === "manual" ? `Entered by ${s.invoiced_override?.set_by || "staff"} ${shortDate(s.invoiced_override?.set_at)}${s.invoiced_override?.note ? ` · ${s.invoiced_override.note}` : ""}` : s.invoiced_basis === "report" ? "From PrintLogic invoice report · net ex-VAT" : "By status change · import the invoice report for exact figures"}
                value={s.invoiced_value} count={s.invoiced_count} countNoun="invoice" previousValue={p?.invoiced_value} accent="#34d399" icon={Receipt}
                action={<InvoicedOverrideButton month={month} override={s.invoiced_override} autoValue={s.invoiced_basis === "manual" ? s.invoiced_auto_value : s.invoiced_value} autoBasis={s.invoiced_basis === "manual" ? s.invoiced_auto_basis : s.invoiced_basis} onSaved={() => load(month)} />}
                footnote={s.invoiced_basis === "manual"
                  ? `Automatic figure (${s.invoiced_auto_basis === "report" ? "invoice report" : "status change"}): ${eur(s.invoiced_auto_value)} · tap the pencil to change or clear`
                  : s.invoiced_basis === "report"
                  ? `${s.credit_note_count ? `${s.credit_note_count} credit note${s.credit_note_count === 1 ? "" : "s"} (${eur(s.credit_note_value)}) included · ` : ""}VAT ${eur(s.invoiced_vat)}${data.invoices_imported_at ? ` · imported ${shortDate(data.invoices_imported_at)}` : ""}`
                  : "Status dates can lag or be back-dated at month end — the imported report is the accurate source"} />
              <MetricCard id="open" label="Not yet invoiced" hint={`All open ${month.slice(0, 4)} jobs (WIP) — not invoiced or cancelled`} value={s.open_value} count={s.open_count} countNoun="open job" accent="#a78bfa" icon={Hourglass}
                footnote={`Of which ${eur(s.open_this_month_value)} from ${s.open_this_month_count} order${s.open_this_month_count === 1 ? "" : "s"} written in ${monthLabel(month, "short")}${s.zero_value_count ? ` · ${s.zero_value_count} job${s.zero_value_count === 1 ? "" : "s"} at €0 need pricing` : ""}`} />
            </div>
            <TrendChart trend={data.trend} selectedMonth={month} />
            <InvoicesPanel invoices={data.invoices || []} month={month} importedAt={data.invoices_imported_at} onImported={() => load(month)} />
            <RepTable rows={data.by_rep} month={month} />
            <div className="grid min-w-0 gap-6 xl:grid-cols-3">
              <DepartmentPanel departments={data.departments || []} />
              <TopSuppliers rows={data.top_suppliers} month={month} totalCost={s.cost_value} />
              <ZeroValuePanel groups={data.zero_value_by_rep} year={month.slice(0, 4)} />
            </div>
            <OrdersTable orders={data.orders} month={month} invoicedBasis={s.invoiced_basis} />
            <p className="text-center text-xs text-slate-600">Figures are order totals synced from PrintLogic every 10 minutes · excludes cancelled orders</p>
          </div>
        )}
      </div>
    </div>
  );
}
